import React, { useState, useEffect, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DinePriceList } from 'api/types';

/**
 * "New version from a date": the effective-row discipline. The current version
 * closes the day before; the new one starts with a copy of its prices (or
 * empty) and can be edited until it comes into force.
 */
interface Props {
  isOpen: boolean;
  list: DinePriceList;
  onSaved: () => void;
  onClose: () => void;
}

const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };

const DineOpsPriceListVersionModal: React.FC<Props> = ({ isOpen, list, onSaved, onClose }) => {
  const [from, setFrom] = useState(tomorrow());
  const [to, setTo] = useState('');
  const [copy, setCopy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (isOpen) { setFrom(tomorrow()); setTo(''); setCopy(true); setError(null); } }, [isOpen]);

  const save = useCallback(async () => {
    if (!from) { setError('A start date is needed.'); return; }
    if (to && to < from) { setError('The last day cannot be before the first.'); return; }
    setSaving(true); setError(null);
    try {
      await dineOpsService.newPriceListVersion(list.id, { validFrom: from, validTo: to || undefined, copyPrices: copy });
      notificationService.success('New version', `'${list.code}' from ${from}. The previous version closes the day before.`);
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'The version could not be created.');
    } finally {
      setSaving(false);
    }
  }, [list, from, to, copy, onSaved]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">New version of {list.code}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={18} /></button>
        </div>
        <div className="modal-content">
          {error && <div className="form-error-message" style={{ marginBottom: 12 }}>{error}</div>}
          <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
            Never edit an effective row — add the next one. The version from {list.validFrom.slice(0, 10)} closes the day before the new start;
            bills raised under it keep its prices. The new version can be edited until it comes into force.
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">New version from <span className="text-danger">*</span></label>
              <input type="date" className="form-input" value={from} min={tomorrow()} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Until <span style={{ opacity: 0.6 }}>(blank = open-ended)</span></label>
              <input type="date" className="form-input" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={copy} onChange={(e) => setCopy(e.target.checked)} />
              <span>Start from the current prices ({list.priceCount}) and change what differs</span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={saving}>{saving ? 'Creating…' : 'Create version'}</button>
        </div>
      </div>
    </>
  );
};

export default DineOpsPriceListVersionModal;
